import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import { AxiosError } from "axios";
import ApproveRequestDropdown from "../../components/Button/ApproveRequestDropdown";
import FormBuilderCancelButton from "../../components/Button/FormBuilderCancelButton";
import FormBuilderSaveAsDraftButton from "../../components/Button/FormBuilderSaveAsDraftButton";
import FormBuilderPopconfirmSubmitButton from "../../components/Button/FormBuilderPopconfirmSubmitButton";
import UpdateClosedReportButton from "../../components/Button/UpdateClosedReportButton";
import FormValidationStatus from "../../components/Form/FormValidationStatus";
import CustomPopconfirm from "../../components/Popover/CustomPopconfirm";
import { ReportingFormFooterProps } from "../../types/reporting.types";
import { alertMessage } from "../../utils/alert";
import { getReportingPermissions } from "../../utils/reportingPermissions";
import {
	notifySyncFailures,
	notifySyncResult,
} from "../../utils/reporting/reportSaveFeedback";
import { validateTenantId } from "../../utils/tenantUtils";

const LessonLearnedReportFormFooter = ({
	form,
	reportContext,
	isSaving,
	isLoading,
	handleClose,
	handleDelete,
	updateMutation,
	saveReport,
	onValidationError,
	onValidationSuccess,
	onExitEditMode,
}: ReportingFormFooterProps) => {
	const {
		status,
		openStatus,
		isAuthor,
		isReviewer,
		isCommenter,
		activeRecordId,
		mode,
	} = reportContext;
	const isSavingOrLoading = isSaving || isLoading;
	const permissions = getReportingPermissions(reportContext);

	const handleRequestChanges = async () => {
		try {
			// Status-only payload: no attachments to sync.
			const data = {
				request_changes_comment: form.formData?.request_changes_comment,
				open_status: "changes_required",
			};

			await updateMutation.mutateAsync({
				id: activeRecordId,
				data: data,
			});

			alertMessage(
				"success",
				`Changes requested and sent back to author for revision.`
			);
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to request changes. Please try again.");
		}
	};

	const handleApprove = async () => {
		try {
			const data = {
				...form.formData,
				...(openStatus === "in_review" && {
					open_status: "final_review",
					approved_on: new Date(),
				}),
				...(openStatus === "final_review" && {
					status: "closed",
					closed_on: new Date(),
				}),
			};

			const { sync } = await saveReport(data, { mode: "edit" });
			notifySyncFailures(sync);
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to approve. Please try again.");
		}
	};

	const handleSaveAsDraft = async () => {
		try {
			const operator = form.formData?.operator;
			if (!validateTenantId(operator)) throw new Error("Invalid operator");

			const data = {
				...form.formData,
				status: "draft",
			};

			const { sync } = await saveReport(data, { mode });
			notifySyncResult(sync, "Lesson learned has been saved to draft.");
			await onExitEditMode?.();
		} catch (error) {
			console.error(error);
			if (error instanceof AxiosError && error?.status === 409) {
				alertMessage(
					"error",
					`This Lesson learned already exists, please ensure the Lesson learned is unique. Changes are not saved.`
				);
			} else {
				alertMessage("error", "Failed to save. Please try again.");
			}
		}
	};

	const handleSubmitForReview = async () => {
		try {
			const operator = form.formData?.operator;
			if (!validateTenantId(operator)) throw new Error("Invalid operator");

			const data = {
				...form.formData,
				status: "open",
				open_status: "in_review",
				submitted_on: new Date(),
			};

			const { sync } = await saveReport(data, { mode });
			notifySyncResult(sync, "Lesson learned has been submitted for review.");
		} catch (error) {
			console.error(error);
			if (error instanceof AxiosError && error?.status === 409) {
				alertMessage(
					"error",
					`This Lesson learned already exists, please ensure the Lesson learned is unique. Changes are not saved.`
				);
			} else {
				alertMessage("error", `Failed to submit for review. Please try again.`);
			}
		}
	};

	const handleUpdate = async () => {
		try {
			const { sync } = await saveReport(
				{ ...form.formData, preserve_status: true },
				{ mode: "edit" }
			);
			notifySyncResult(sync, "Lesson learned has been updated.");
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to update. Please try again.");
		}
	};

	return (
		<>
			{/**
			 * Delete button
			 * Conditions:
			 * 1. Edit mode (saved in db)
			 * 2. By author when report is still in draft mode
			 * 3. By reviewer when report is being reviewed (in_review) and is not closed
			 */}
			{mode === "edit" && permissions.canDelete ? (
				<CustomPopconfirm
					title="Permanently delete this item? This action cannot be undone."
					handleConfirm={handleDelete}
				>
					<Button
						className="ant-btn-secondary"
						danger
						disabled={isSavingOrLoading}
					>
						Delete
					</Button>
				</CustomPopconfirm>
			) : (
				<div />
			)}
			{/* Flex spacer to prevent overlap between Delete button and validation message/buttons */}
			<div style={{ flex: 1, minWidth: 24 }} />
			<div className="flex gap-2 items-center">
				<FormValidationStatus
					form={form}
					reportType="lesson_learned"
					mode={mode}
				/>
				{/**
				 * Close / Cancel button
				 * Label switches based on unsaved changes:
				 * - Clean form: "Close" closes the drawer immediately.
				 * - Dirty form: "Cancel" prompts to discard unsaved changes.
				 */}
				<FormBuilderCancelButton
					form={form}
					handleFormCancel={handleClose}
					alwaysEnabled={true}
					isLoading={isSavingOrLoading}
				/>
				{/**
				 * Save as draft button
				 * Conditions:
				 * 1. Only shown to Author when report is in create mode, draft mode or to be changed (changes_required)
				 */}
				{permissions.canSubmit &&
					(mode === "create" ||
						status === "draft" ||
						openStatus === "changes_required") && (
						<FormBuilderSaveAsDraftButton
							form={form}
							fieldsToWatch={["title"]}
							handleFormSave={handleSaveAsDraft}
							isLoading={isSavingOrLoading}
						/>
					)}

				{/**
				 * Submit for review button
				 * Conditions:
				 * 1. Only shown to Author when report is in create mode, draft mode or to be changed (changes_required)
				 */}
				{isAuthor &&
					(mode === "create" ||
						status === "draft" ||
						openStatus === "changes_required") && (
						<FormBuilderPopconfirmSubmitButton
							form={form}
							labelText="Submit for review"
							title={
								<div className="max-w-2xs p-2.5">
									Submit for review? No further edits can be done unless passed
									back.
								</div>
							}
							onConfirm={handleSubmitForReview}
							isLoading={isSavingOrLoading}
							onValidationError={onValidationError}
						/>
					)}
				{/**
				 * Update button
				 * Conditions:
				 * 1. Only shown when report is not draft and admin is editing
				 */}
				{mode === "edit" && status !== "draft" && permissions.canAdminSave && (
					<UpdateClosedReportButton
						form={form}
						isSavingOrLoading={isSavingOrLoading}
						handleUpdate={handleUpdate}
						onValidationError={onValidationError}
						onValidationSuccess={onValidationSuccess}
						status={status}
					/>
				)}

				{/**
				 * Approve + Request changes dropdown
				 * Conditions:
				 * 1. Reviewer when report is open and in_review
				 * 2. Reviewer or commenter when report is open and final_review
				 */}
				<ApproveRequestDropdown
					form={form}
					isSavingOrLoading={isSavingOrLoading}
					handleApprove={handleApprove}
					handleRequestChanges={handleRequestChanges}
					onValidationError={onValidationError}
					approveLabel={
						openStatus === "in_review" ? "Approve" : "Save and close"
					}
					approveTitle={
						<div className="max-w-2xs p-2.5 text-justify">
							{openStatus === "in_review"
								? "Approve lesson learned report? The report will stay open. The assigned user will be notified to complete and close the lesson."
								: "Save 'What was done'? Your input will be saved. This lesson will be marked as closed, and shared to all tenants."}
						</div>
					}
					requestChangesContent={
						<div className="flex flex-col gap-2">
							<div className="flex flex-nowrap gap-2">
								<ExclamationCircleOutlined />
								<span className="text-justify flex items-center gap-2">
									Assigned roles won’t be notified. This report will return to
									the author for revision. Please add a comment to explain why.
								</span>
							</div>
							<Input.TextArea
								autoSize={{ minRows: 3 }}
								placeholder="Comments"
								value={form.formData?.request_changes_comment}
								onChange={(e) =>
									form.setFormData({
										...form.formData,
										request_changes_comment: e.target.value,
									})
								}
							/>
						</div>
					}
					canApprove={
						status === "open" &&
						((permissions.canApprove && openStatus === "in_review") ||
							((permissions.canApprove || isCommenter) &&
								openStatus === "final_review"))
					}
					canRequestChanges={
						permissions.canRequestChanges &&
						status === "open" &&
						openStatus === "in_review"
					}
				/>
			</div>
		</>
	);
};

export default LessonLearnedReportFormFooter;
